// Static mock data for the landing-page Signal Flow diagram.
// Mirrors the shape of the production SignalFlowNetwork data but decoupled
// from real types so landing has zero runtime dependency on @/types.

export const MAX_VISIBLE_SIGNALS = 10;
export const MAX_VISIBLE_SENSORS = 8;

// ─── Shared SVG layout constants ──────────────────────────────────────
// Used by both the SVG renderer (LandingSignalFlow) and the scroll
// orchestrator (ScrollyHero) to compute zoom/pan focus targets. Single
// source of truth prevents the two files from drifting out of sync.
export const VIEW_W = 860;
export const SRC_X = 10,  SRC_W = 85,  SRC_H = 32;
export const SIG_X = 170, SIG_W = 120, SIG_H = 28;
export const INT_X = 420, INT_W = 160, INT_H = 44;
export const INS_X = 700, INS_W = 140, INS_H = 80;
export const DIV1_X = 140, DIV2_X = 370, DIV3_X = 660;

// Column center-x — derived from the X/W pairs above.
export const SRC_CX = SRC_X + SRC_W / 2;
export const SIG_CX = SIG_X + SIG_W / 2;
export const INT_CX = INT_X + INT_W / 2;
export const INS_CX = INS_X + INS_W / 2;

export interface SignalMetadata {
  label: string;
  unit: string;
  sources: string[];
}

export interface Contribution {
  signal: string;
  raw_value: number;
  risk_score: number;
  weighted_contribution: number;
  direction: 'rising' | 'falling' | 'stable';
  explanation: string;
}

export interface InteractionNode {
  id: string;
  label: string;
  description: string;
  signals: string[];
}

// --- Source groups (digital-twin story) ------------------------------------
export const SOURCE_LABELS: Record<string, string> = {
  wearable:    'Wearables',
  force_plate: 'Force Plates',
  checkin:     'Check-ins',
  training:    'Training Load',
};

export const SOURCE_ORDER: string[] = ['wearable', 'force_plate', 'checkin', 'training'];

// --- Signal metadata -------------------------------------------------------
export const SIGNAL_METADATA: Record<string, SignalMetadata> = {
  hrv_trend_7d:    { label: 'HRV Trend (7d)',    unit: 'ms/day', sources: ['wearable'] },
  rhr_trend_7d:    { label: 'RHR Trend (7d)',    unit: 'bpm/d',  sources: ['wearable'] },
  hrv_rmssd:       { label: 'HRV (RMSSD)',       unit: 'ms',     sources: ['wearable'] },
  rhr_zscore:      { label: 'RHR Z-Score',       unit: 'SD',     sources: ['wearable'] },
  sleep_score:     { label: 'Sleep Score',       unit: '/100',   sources: ['wearable'] },
  cmj_asymmetry:   { label: 'CMJ Asym L/R',      unit: '%',      sources: ['force_plate'] },
  soreness_3d:     { label: 'Soreness 3d',       unit: '/10',    sources: ['checkin'] },
  training_load:   { label: 'Training Load',     unit: 'AU',     sources: ['training'] },
  load_volatility: { label: 'Load Volatility',   unit: 'CV',     sources: ['training'] },
  acwr:            { label: 'Acute:Chronic',     unit: 'ratio',  sources: ['training'] },
};

// --- Contributions (Cole Sprout -- 10 signals, 4 sources) ------------------
export const CONTRIBUTIONS: Contribution[] = [
  { signal: 'hrv_trend_7d',    raw_value: -6.5,    risk_score: 0.62, weighted_contribution: 0.09, direction: 'rising',  explanation: '' },
  { signal: 'rhr_trend_7d',    raw_value:  0.5,    risk_score: 0.55, weighted_contribution: 0.05, direction: 'rising',  explanation: '' },
  { signal: 'cmj_asymmetry',   raw_value:  8.4,    risk_score: 0.58, weighted_contribution: 0.08, direction: 'rising',  explanation: '' },
  { signal: 'soreness_3d',     raw_value:  6,      risk_score: 0.52, weighted_contribution: 0.03, direction: 'rising',  explanation: '' },
  { signal: 'rhr_zscore',      raw_value: -0.1,    risk_score: 0.42, weighted_contribution: 0.04, direction: 'stable',  explanation: '' },
  { signal: 'hrv_rmssd',       raw_value:  74,     risk_score: 0.35, weighted_contribution: 0.02, direction: 'falling', explanation: '' },
  { signal: 'training_load',   raw_value:  6209.9, risk_score: 0.58, weighted_contribution: 0.02, direction: 'rising',  explanation: '' },
  // sleep_score deliberately below the 0.15 green threshold so the node and its
  // edges render as protective (green). Still visible via weighted_contribution > 0.005.
  { signal: 'sleep_score',     raw_value:  82,     risk_score: 0.08, weighted_contribution: 0.01, direction: 'rising',  explanation: '' },
  { signal: 'load_volatility', raw_value:  1298,   risk_score: 0.31, weighted_contribution: 0.01, direction: 'rising',  explanation: '' },
  { signal: 'acwr',            raw_value:  1.34,   risk_score: 0.35, weighted_contribution: 0.01, direction: 'stable',  explanation: '' },
];

// --- Interactions ----------------------------------------------------------
export const INTERACTION_NODES: InteractionNode[] = [
  { id: 'recovery_deficit',  label: 'Recovery Deficit',  description: 'Suppressed autonomic',     signals: ['hrv_trend_7d', 'rhr_zscore', 'hrv_rmssd', 'sleep_score'] },
  { id: 'overload_risk',     label: 'Overload Risk',     description: 'Acute load elevation',     signals: ['training_load', 'load_volatility', 'acwr', 'soreness_3d'] },
  { id: 'autonomic_stress',  label: 'Autonomic Stress',  description: 'Sympathetic dominance',    signals: ['rhr_trend_7d', 'hrv_trend_7d', 'cmj_asymmetry'] },
];

export const INTERACTION_PENALTIES: string[] = [
  'Recovery deficit: low sleep + declining HRV (x1.2)',
  'Overload risk: training load spike + ACWR (x1.2)',
  'Autonomic stress: RHR elevated + HRV declining (x1.1)',
];

// --- Helpers ---------------------------------------------------------------

/**
 * Filter contributions by visibility threshold, sort by weight descending,
 * and cap to MAX_VISIBLE_SIGNALS.
 */
export function selectVisibleContributions(all: Contribution[]): Contribution[] {
  return all
    .filter((c) => c.risk_score > 0.1 || c.weighted_contribution > 0.005)
    .sort((a, b) => b.weighted_contribution - a.weighted_contribution)
    .slice(0, MAX_VISIBLE_SIGNALS);
}

/** Extract x-multiplier from a penalty string like "... (x1.2)". */
export function extractMultiplier(penalty: string): number | null {
  const match = penalty.match(/\(x([\d.]+)\)/);
  return match ? parseFloat(match[1]) : null;
}

/** Format a signal value with its unit suffix. */
export function formatWithUnit(_signal: string, value: number, meta: SignalMetadata): string {
  const u = meta.unit || '';
  if (u === 'ms')       return value.toFixed(0) + 'ms';
  if (u === 'ms/day')   return value.toFixed(1) + ' ms/day';
  if (u === 'bpm/d')    return value.toFixed(1) + ' bpm/d';
  if (u === '/100')     return value.toFixed(0) + ' /100';
  if (u === '/10')      return value.toFixed(0) + '/10';
  if (u === '%')        return value.toFixed(1) + '%';
  if (u === 'SD')       return value.toFixed(1) + ' SD';
  if (u === 'AU')       return value.toFixed(1) + ' AU';
  if (u === 'CV')       return value.toFixed(1) + ' CV';
  if (u === 'ratio')    return value.toFixed(2);
  return value.toFixed(1);
}

// --- AI Insight narrative (stage 4 reveal) ---------------------------------
export const INSIGHT_HEADLINE =
  "Cole Sprout's current biometric profile indicates a moderate injury risk, primarily driven by signs of overtraining and autonomic imbalance.";

export const INSIGHT_NARRATIVE =
  "HRV trend significantly suppressed while RHR trend elevated. HRV decline + load surplus -- applying a x1.20 multiplier to the composite score. Sleep quality and Sleep duration within normal range.";

// Static demo copy — the 2026-04-21 date is intentional, not wired to a live source.
export const INSIGHT_META =
  "10 signals | 87% confidence | 2026-04-21 | Data quality is good, with a solid baseline established over 76 healthy days.";

export const INSIGHT_RISK = { score: 38, tier: 'MODERATE', previousScore: 18 };
